import { useCallback } from 'react';

import { useTracking } from '@skyscanner/strevda-tracking';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import { getStrevdaTrackingConfig } from '@web-platform/shared-universal-tracking/src/getStrevdaTrackingConfig';
import { trackSmartMetric } from '@web-platform/shared-universal-tracking/src/trackExperiment';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { logOperationalEvent } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import { CollapsedHeader } from '../CollapsableBanner/CollapsedHeader/CollapsedHeader';
import { ExpandedBanner } from '../CollapsableBanner/ExpandedBanner/ExpandedBanner';

import type { FilteredExploreAirline } from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/filteredExploreAirline';

import STYLES from './FilteredExploreAirline.module.scss';

type FilteredExploreAirlineProps = {
  content: FilteredExploreAirline;
  isFilterActive: boolean;
  onFilterToggle?: (isEnabled: boolean) => void;
};

const FilteredExploreAirlineAd = ({
  content,
  isFilterActive,
  onFilterToggle,
}: FilteredExploreAirlineProps) => {
  const { analyticsProperties, creative, performanceEventsPayloads, pixels } = content;
  const partnerId = creative.partnerId ?? '';
  const creativeId = analyticsProperties.idCreative;

  const { ref: trackingRef, trackClick } = useTracking<HTMLDivElement>(
    { analyticsProperties, pixels, performanceEventsPayloads },
    {},
    false,
    getStrevdaTrackingConfig(),
  );

  const handleToggle = useCallback(() => {
    if (!isFilterActive) {
      trackClick();
    }
    const eventName = isFilterActive
      ? ANALYTICS_EVENTS.INTERACTION.EXPLORE_BRAND_AIRLINES_FILTER_DISABLED.NAME
      : ANALYTICS_EVENTS.INTERACTION.EXPLORE_BRAND_AIRLINES_FILTER_ENABLED.NAME;

    trackSmartMetric(
      isFilterActive
        ? ANALYTICS_EVENTS.SMART_METRICS.EXPLORE_BRAND_AIRLINES_FILTER_DISABLED.NAME
        : ANALYTICS_EVENTS.SMART_METRICS.EXPLORE_BRAND_AIRLINES_FILTER_ENABLED.NAME,
    );
    trackInteractionEvent({
      name: eventName,
      strings: { partner_id: partnerId, creative_id: creativeId },
    });
    logOperationalEvent({
      eventName,
      partnerId,
      creativeId,
    });

    onFilterToggle?.(!isFilterActive);
  }, [isFilterActive, onFilterToggle, trackClick, partnerId, creativeId]);

  return (
    <div className={STYLES.wrapper} data-testid="filtered-explore-airline" ref={trackingRef}>
      <div className={isFilterActive ? STYLES.hidden : undefined}>
        <CollapsedHeader
          heroImageUrl={creative.heroImage}
          partnerLogo={creative.partnerLogo}
          mobilePartnerLogo={creative.mobilePartnerLogo || creative.partnerLogo}
          partnerName={creative.partnerName}
          titleText={creative.titleText}
          isToggleOn={isFilterActive}
          onToggleChange={handleToggle}
          pioPartnerName={creative.pioPartnerName ?? ''}
          pioAgencyName={creative.pioAgencyName ?? ''}
        />
      </div>
      <div className={isFilterActive ? undefined : STYLES.hidden}>
        <ExpandedBanner
          heroImageUrl={creative.heroImage}
          partnerLogo={creative.partnerLogo}
          partnerName={creative.partnerName}
          titleText={creative.titleText}
          subtitleText={creative.subtitleText}
          isToggleOn={isFilterActive}
          onToggleChange={handleToggle}
          pioPartnerName={creative.pioPartnerName ?? ''}
          pioAgencyName={creative.pioAgencyName ?? ''}
        />
      </div>
    </div>
  );
};

export { FilteredExploreAirlineAd };
